import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import Modal from './Modal.js'
import AnalysisBuilder from './AnalysisBuilderLazy.js'
import type { AnalysisBuilderRef, AnalysisBuilderInitialFunnelState, AnalysisBuilderInitialFlowState, AnalysisBuilderInitialRetentionState } from './AnalysisBuilder/types.js'
import type { PortletConfig, ColorPalette, CubeQuery, MultiQueryConfig, DashboardFilter, AnalysisType } from '../types.js'
import type { AnalysisConfig } from '../types/analysisConfig.js'
import { ensureAnalysisConfig } from '../utils/configMigration.js'
import { funnelModeAdapter } from '../adapters/funnelModeAdapter.js'
import { flowModeAdapter } from '../adapters/flowModeAdapter.js'
import { retentionModeAdapter } from '../adapters/retentionModeAdapter.js'
import { useTranslation } from '../hooks/useTranslation.js'
import { analysisConfigHasContent, getEmptyContentMessage } from './portletAnalysisModal/saveValidation.js'

interface PortletAnalysisModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (portlet: PortletConfig | Omit<PortletConfig, 'id' | 'x' | 'y'>) => void
  portlet?: PortletConfig | null
  /** Initial data to display (avoids re-fetching when editing) */
  initialData?: any[]
  title: string
  submitText: string
  colorPalette?: ColorPalette
  /** @deprecated Dashboard filters are no longer merged into the editor - they are applied at execution time */
  dashboardFilters?: DashboardFilter[]
}

/**
 * PortletAnalysisModal - A modal wrapper around AnalysisBuilder for portlet editing
 *
 * This replaces PortletEditModal with the modern AnalysisBuilder interface.
 * Features:
 * - Two-panel layout with results and query builder
 * - Auto-execution of queries
 * - Smart chart defaults
 * - Title input in header
 * - Initial data support (no re-fetch when editing)
 */
export default function PortletAnalysisModal({
  isOpen,
  onClose,
  onSave,
  portlet,
  initialData,
  title: modalTitle,
  submitText,
  colorPalette
}: PortletAnalysisModalProps) {
  const { t } = useTranslation()

  // Ref to AnalysisBuilder for getting current query and chart config
  const builderRef = useRef<AnalysisBuilderRef>(null)

  // Title state
  const [formTitle, setFormTitle] = useState('')

  // =========================================================================
  // Load from analysisConfig (migrating from legacy format if needed)
  // =========================================================================
  const derivedConfig = useMemo<AnalysisConfig | null>(() => {
    if (!portlet) return null

    // Use ensureAnalysisConfig which handles both new and legacy formats
    const normalizedPortlet = ensureAnalysisConfig(portlet)
    return normalizedPortlet.analysisConfig
  }, [portlet])

  // Parse initial query from derived config
  // Dashboard filters are NOT merged here - they are applied at execution time
  // by AnalyticsPortlet.tsx. The editor shows only the portlet's own filters
  // to prevent dashboard filters from leaking into saved portlet config.
  const initialQuery = useMemo<CubeQuery | MultiQueryConfig | undefined>(() => {
    if (!derivedConfig) return undefined

    // Get query from derived config
    const query = derivedConfig.query
    if (!query) return undefined

    // Handle funnel mode - return the ServerFunnelQuery as-is
    if (derivedConfig.analysisType === 'funnel') {
      return query as CubeQuery | MultiQueryConfig
    }

    // Return query as-is (single CubeQuery or MultiQueryConfig)
    return query as CubeQuery | MultiQueryConfig
  }, [derivedConfig])

  // Initial chart config from derived config
  const initialChartConfig = useMemo(() => {
    if (!derivedConfig) return undefined

    // Get chart config for current mode
    const modeCharts = derivedConfig.charts[derivedConfig.analysisType]
    if (!modeCharts) return undefined

    return {
      chartType: modeCharts.chartType,
      chartConfig: modeCharts.chartConfig,
      displayConfig: modeCharts.displayConfig
    }
  }, [derivedConfig])

  // Initial analysis type from derived config
  const initialAnalysisType: AnalysisType | undefined = derivedConfig?.analysisType

  // Initial funnel state from portlet (when analysisType === 'funnel')
  // Note: The funnel query data is in derivedConfig.query, but we need to pass
  // the store-level funnel state (funnelCube, funnelSteps, etc.) separately
  const initialFunnelState: AnalysisBuilderInitialFunnelState | undefined = useMemo(() => {
    if (derivedConfig?.analysisType !== 'funnel') return undefined

    // Option 1: Use legacy fields if present (backward compatibility)
    if (portlet?.funnelSteps && portlet.funnelSteps.length > 0) {
      return {
        funnelCube: portlet.funnelCube,
        funnelSteps: portlet.funnelSteps,
        funnelTimeDimension: portlet.funnelTimeDimension,
        funnelBindingKey: portlet.funnelBindingKey,
        funnelChartType: portlet.funnelChartType,
        funnelChartConfig: portlet.funnelChartConfig,
        funnelDisplayConfig: portlet.funnelDisplayConfig,
      }
    }

    // Option 2: Parse from analysisConfig.query (Phase 4 implementation)
    // This handles the case where only analysisConfig exists (no legacy fields)
    if (derivedConfig.query && 'funnel' in derivedConfig.query) {
      // Use adapter's conversion logic - already handles all formats
      const funnelState = funnelModeAdapter.load(derivedConfig)
      const chartConfig = derivedConfig.charts?.funnel

      return {
        ...funnelState,
        funnelChartType: chartConfig?.chartType,
        funnelChartConfig: chartConfig?.chartConfig,
        funnelDisplayConfig: chartConfig?.displayConfig,
      }
    }

    return undefined
  }, [derivedConfig, portlet])

  // Initial flow state from portlet (when analysisType === 'flow')
  const initialFlowState: AnalysisBuilderInitialFlowState | undefined = useMemo(() => {
    if (derivedConfig?.analysisType !== 'flow') return undefined

    // Parse from analysisConfig.query
    if (derivedConfig.query && 'flow' in derivedConfig.query) {
      // Use adapter's conversion logic
      const flowState = flowModeAdapter.load(derivedConfig)
      const chartConfig = derivedConfig.charts?.flow

      return {
        ...flowState,
        flowChartType: chartConfig?.chartType,
        flowChartConfig: chartConfig?.chartConfig,
        flowDisplayConfig: chartConfig?.displayConfig,
      }
    }

    return undefined
  }, [derivedConfig])

  // Initial retention state from portlet (when analysisType === 'retention')
  const initialRetentionState: AnalysisBuilderInitialRetentionState | undefined = useMemo(() => {
    if (derivedConfig?.analysisType !== 'retention') return undefined

    // Parse from analysisConfig.query
    if (derivedConfig.query && 'retention' in derivedConfig.query) {
      // Use adapter's conversion logic
      const retentionState = retentionModeAdapter.load(derivedConfig)
      const chartConfig = derivedConfig.charts?.retention

      return {
        ...retentionState,
        retentionChartType: chartConfig?.chartType,
        retentionChartConfig: chartConfig?.chartConfig,
        retentionDisplayConfig: chartConfig?.displayConfig,
      }
    }

    return undefined
  }, [derivedConfig])

  // Reset form state when modal opens/closes or portlet changes
  useEffect(() => {
    if (isOpen) {
      setFormTitle(portlet?.title || '')
    }
  }, [isOpen, portlet])

  // Handle save - Phase 4: Only save analysisConfig (no more dual-write)
  const handleSave = useCallback(() => {
    if (!formTitle.trim()) {
      alert('Please enter a title for the portlet.')
      return
    }

    // Get AnalysisConfig from store - this is the only format we save now
    const analysisConfig = builderRef.current?.getAnalysisConfig()

    if (!analysisConfig) {
      alert('Please configure a query before saving.')
      return
    }

    // Validate content based on analysis type
    const { query, analysisType } = analysisConfig

    if (!analysisConfigHasContent(query)) {
      alert(getEmptyContentMessage(analysisType))
      return
    }

    // Build portlet config with ONLY analysisConfig (no more legacy fields)
    const portletData: PortletConfig | Omit<PortletConfig, 'id' | 'x' | 'y'> = {
      ...(portlet || {}),
      title: formTitle.trim(),

      // === Canonical format - single source of truth ===
      analysisConfig,

      // Preserve dashboard filter mapping and eager load settings
      dashboardFilterMapping: portlet?.dashboardFilterMapping,
      eagerLoad: portlet?.eagerLoad,

      // Preserve existing position or use defaults for new portlets
      w: portlet?.w || 5,
      h: portlet?.h || 4
    } as PortletConfig

    onSave(portletData)
    onClose()
  }, [formTitle, portlet, onSave, onClose])

  // Handle cancel
  const handleCancel = useCallback(() => {
    onClose()
  }, [onClose])

  // Footer with save/cancel buttons
  const footer = (
    <>
      <button
        type="button"
        onClick={handleCancel}
        className="dc:px-4 dc:py-2 dc:text-sm dc:font-medium text-dc-text-secondary hover:text-dc-text bg-dc-surface dc:border border-dc-border dc:rounded-md hover:bg-dc-surface-hover dc:transition-colors"
      >
        {t('common.actions.cancel')}
      </button>
      <button
        type="button"
        onClick={handleSave}
        className="dc:px-4 dc:py-2 dc:text-sm dc:font-medium text-white bg-dc-accent hover:bg-dc-accent-hover dc:rounded-md dc:transition-colors"
      >
        {submitText}
      </button>
    </>
  )

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      size="fullscreen-mobile"
      showCloseButton={true}
      closeOnBackdropClick={false}
      closeOnEscape={true}
      noPadding={true}
      footer={footer}
    >
      {/* Custom content with title input */}
      <div className="dc:flex dc:flex-col dc:h-full">
        {/* Title input section */}
        <div className="dc:shrink-0 dc:px-4 dc:py-3 dc:border-b border-dc-border bg-dc-surface-secondary">
          <div className="dc:flex dc:items-center dc:gap-3">
            <label htmlFor="portlet-title" className="dc:text-sm dc:font-medium text-dc-text-secondary dc:shrink-0">
              {t('common.labels.title')}
            </label>
            <input
              id="portlet-title"
              type="text"
              value={formTitle}
              onChange={(e) => setFormTitle(e.target.value)}
              placeholder="Enter portlet title..."
              autoComplete="off"
              className="dc:flex-1 dc:px-3 dc:py-1.5 dc:text-sm bg-dc-surface dc:border border-dc-border dc:rounded-md text-dc-text placeholder-dc-text-muted dc:focus:outline-none dc:focus:ring-2 focus:ring-dc-accent focus:border-transparent"
              autoFocus
            />
          </div>
        </div>

        {/* AnalysisBuilder content */}
        <div className="dc:flex-1 dc:min-h-0">
          <AnalysisBuilder
            ref={builderRef}
            maxHeight="100%"
            initialQuery={initialQuery}
            initialChartConfig={initialChartConfig}
            initialAnalysisType={initialAnalysisType}
            initialFunnelState={initialFunnelState}
            initialFlowState={initialFlowState}
            initialRetentionState={initialRetentionState}
            initialData={initialData}
            colorPalette={colorPalette}
            disableLocalStorage={true}
            className="dc:h-full"
          />
        </div>
      </div>
    </Modal>
  )
}
